import { useState } from "react";
import { Box, Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiErrorToast } from "@/hooks/useApiErrorToast";
import { useCatalogoDisparo } from "@/hooks/useCatalogoDisparo";
import {
  DISPARO_TEMPLATE_QUERY_KEY,
  DISPARO_TEMPLATES_QUERY_KEY,
  useDisparoTemplates,
} from "@/hooks/useDisparos";
import {
  CriarDisparoTemplateDto,
  DisparoTemplateItem,
} from "@/models/consignado/disparo.model";
import {
  atualizarDisparoTemplate,
  criarDisparoTemplate,
  removerDisparoTemplate,
} from "@/services/consignado/disparo.service";
import TemplateScreen, { type TemplateScreenMode } from "./TemplateScreen";
import TemplatesView, { type TemplatesFiltros } from "./TemplatesView";

/** O PUT substitui o template inteiro — é o corpo de uma linha da lista. */
const dtoDoItem = (t: DisparoTemplateItem): CriarDisparoTemplateDto => ({
  nome: t.nome,
  ativo: t.ativo,
  contexto: t.contexto,
  canal: t.canal,
  provedor: t.provedor,
  template: t.conteudo,
});

/**
 * Templates reutilizáveis ligados na API real (HIS-APP-notificacoes-007,
 * consignado-trabalhador). Não tem rota própria: abre pelo botão "Templates"
 * da Configuração de Disparos (DisparosContainer).
 *
 * Chamadas:
 *  - catálogo: os seis GET /disparos/catalogo/* (useCatalogoDisparo) — o
 *              editor da mensagem é o mesmo do disparo e precisa deles
 *  - grade:    GET /disparos/templates, filtrado no servidor, sem paginação
 *  - escrita:  POST, PUT /:id e DELETE /:id em /disparos/templates
 *
 * Diferente do disparo, o template não tem `versao` nem PATCH de ativação:
 * ligar e desligar é um PUT com o template inteiro, que o backend revalida.
 *
 * Editar um template muda os disparos que o usam — o vínculo é vivo (ver
 * TemplateReutilizavelCampo).
 */
type Props = {
  /** Volta para a Configuração de Disparos, que é onde os templates moram. */
  onVoltar?: () => void;
};

export default function TemplatesContainer({ onVoltar }: Props) {
  const toast = useToast();
  const toastErro = useApiErrorToast();
  const queryClient = useQueryClient();

  const [filtros, setFiltros] = useState<TemplatesFiltros>({});

  const [view, setView] = useState<"lista" | "form">("lista");
  const [formMode, setFormMode] = useState<TemplateScreenMode>("create");
  // Guarda a linha, não o id: se a lista recarregar com o formulário aberto,
  // o TemplateScreen receberia um objeto novo e descartaria o rascunho.
  const [selecionado, setSelecionado] = useState<DisparoTemplateItem | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const catalogo = useCatalogoDisparo();
  const lista = useDisparoTemplates(filtros);

  // O template aberto como "origem" num disparo também é cacheado por id;
  // depois de editar ou excluir, ele precisa refletir o que está no servidor.
  const invalidar = (id?: string) => {
    queryClient.invalidateQueries({ queryKey: [DISPARO_TEMPLATES_QUERY_KEY] });
    if (id) queryClient.invalidateQueries({ queryKey: [DISPARO_TEMPLATE_QUERY_KEY, id] });
  };

  const salvar = useMutation({
    mutationFn: (dto: CriarDisparoTemplateDto) =>
      formMode === "edit" && selecionado
        ? atualizarDisparoTemplate(selecionado.id, dto)
        : criarDisparoTemplate(dto),
    onSuccess: (salvo) => {
      toast({
        title: formMode === "edit" ? "Template atualizado." : "Template criado.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      invalidar(salvo?.id);
      setView("lista");
    },
    onError: toastErro(
      "Não foi possível salvar o template.",
      "Confira os campos e tente de novo.",
    ),
  });

  const alternarAtivo = useMutation({
    mutationFn: (item: DisparoTemplateItem) =>
      atualizarDisparoTemplate(item.id, { ...dtoDoItem(item), ativo: !item.ativo }),
    onMutate: (item) => setTogglingId(item.id),
    onSuccess: (_, item) => invalidar(item.id),
    // O PUT revalida o conteúdo contra o catálogo: template gravado antes de
    // uma mudança no catálogo pode ser recusado aqui.
    onError: toastErro(
      "Não foi possível alterar o template.",
      "O backend revalida o template inteiro — abra-o para conferir o conteúdo.",
    ),
    onSettled: () => setTogglingId(null),
  });

  const excluir = useMutation({
    mutationFn: (item: DisparoTemplateItem) => removerDisparoTemplate(item.id),
    onSuccess: (_, item) => {
      toast({ title: "Template excluído.", status: "success", duration: 3000, isClosable: true });
      invalidar(item.id);
    },
    onError: toastErro("Não foi possível excluir o template."),
  });

  if (catalogo.isLoading) {
    return (
      <Flex w="100%" justify="center" py={16}>
        <Spinner color="primary" />
      </Flex>
    );
  }

  if (catalogo.isError) {
    return (
      <Box w="100%" p={{ base: 4, md: 8 }}>
        <Text color="red.500" fontFamily="Lexend Deca">
          Não foi possível carregar o catálogo de disparos. Sem ele o template não pode ser montado.
        </Text>
      </Box>
    );
  }

  if (view === "form") {
    return (
      <TemplateScreen
        mode={formMode}
        template={selecionado}
        catalogo={catalogo.data}
        isSaving={salvar.isPending}
        onBack={() => setView("lista")}
        // A promessa trava o Salvar até o POST/PUT responder. A falha já vira
        // toast no onError da mutation — aqui só não pode estourar.
        onSave={(dto) => salvar.mutateAsync(dto).catch(() => undefined)}
      />
    );
  }

  return (
    <TemplatesView
      templates={lista.data ?? []}
      catalogo={catalogo.data}
      filtros={filtros}
      isLoading={lista.isLoading}
      isError={lista.isError}
      togglingId={togglingId}
      onFiltrosChange={setFiltros}
      onNovo={() => {
        setSelecionado(null);
        setFormMode("create");
        setView("form");
      }}
      onEditar={(t) => {
        setSelecionado(t);
        setFormMode("edit");
        setView("form");
      }}
      onExcluir={(t) => excluir.mutate(t)}
      onToggleAtivo={(t) => alternarAtivo.mutate(t)}
      onVoltar={onVoltar}
    />
  );
}
